/**
 * Project calendar screen.
 * Route: /schedule/calendar
 * Composes the calendar tabs. Tab panels live in components/.
 */
import React from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Group,
  Modal,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCalendarTime,
  IconClockEdit,
  IconListSearch,
  IconPlus,
  IconRefresh,
} from '@tabler/icons-react';
import { getProjectId } from '../../utils/cookies';
import { getProjectDisplayName } from '../../utils/projectDisplay';
import {
  PermissionRestrictedTooltip,
  useCanSchedulerAction,
} from '../../permissions/scheduler';
import {
  type CalendarDayRecord,
  type CalendarExceptionRecord,
  type CalendarRecord,
  type CalendarWeekRecord,
  createCalendar,
  createCalendarDay,
  createCalendarException,
  createCalendarWeek,
  deleteCalendar,
  deleteCalendarDay,
  deleteCalendarException,
  deleteCalendarWeek,
  fetchCalendarDetails,
  fetchProjectCalendars,
  updateCalendar,
  updateCalendarDay,
  updateCalendarException,
  updateCalendarWeek,
} from '../../api/schedulingApis';
import CalendarExceptionsTab from './components/CalendarExceptionsTab';
import CalendarOverviewTab from './components/CalendarOverviewTab';
import CalendarOverridesTab from './components/CalendarOverridesTab';
import CalendarWeekRulesTab from './components/CalendarWeekRulesTab';
import { useFeatureTipsOptional } from '../feature-tips';

const DAY_OPTIONS = [
  { value: '1', label: 'Sunday' },
  { value: '2', label: 'Monday' },
  { value: '3', label: 'Tuesday' },
  { value: '4', label: 'Wednesday' },
  { value: '5', label: 'Thursday' },
  { value: '6', label: 'Friday' },
  { value: '7', label: 'Saturday' },
];

const TZ_OPTIONS = [
  'UTC',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Europe/London',
  'America/New_York',
].map((v) => ({ value: v, label: v }));

const CALENDAR_TYPE_OPTIONS = [
  { value: 'global', label: 'Global' },
  { value: 'project', label: 'Project' },
  { value: 'resource', label: 'Resource' },
];

function formatDateYmd(value: Date | string | null): string {
  if (!value) return '';
  const date =
    value instanceof Date
      ? value
      : new Date(typeof value === 'string' ? value : String(value));
  if (Number.isNaN(date.getTime())) return '';
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function parseDateYmd(value: string): Date | null {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

const TABLE_VIEWPORT_HEIGHT = 'calc(100vh - 300px)';

const CalendarPage: React.FC = () => {
  const projectId = getProjectId();
  const projectName = getProjectDisplayName();
  const featureTips = useFeatureTipsOptional();
  const canCalendarCreate = useCanSchedulerAction('calendarCreate');
  const canCalendarEdit = useCanSchedulerAction('calendarEdit');
  const canCalendarDelete = useCanSchedulerAction('calendarDelete');
  const hasLoadedCalendarListRef = React.useRef(false);

  React.useEffect(() => {
    featureTips?.maybeShowTip('calendar');
  }, [featureTips?.maybeShowTip]);

  const [calendarIdInput, setCalendarIdInput] = React.useState('');
  const [calendarOptions, setCalendarOptions] = React.useState<
    Array<{ value: string; label: string; calendarId: string; isDefault: boolean }>
  >([]);
  const [loadingCalendarOptions, setLoadingCalendarOptions] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [copyCalendarName, setCopyCalendarName] = React.useState('');
  const [createCalendarModalOpened, setCreateCalendarModalOpened] = React.useState(false);

  const [calendar, setCalendar] = React.useState<CalendarRecord | null>(null);
  const [weeks, setWeeks] = React.useState<CalendarWeekRecord[]>([]);
  const [exceptions, setExceptions] = React.useState<CalendarExceptionRecord[]>([]);
  const [days, setDays] = React.useState<CalendarDayRecord[]>([]);

  const [calendarDraft, setCalendarDraft] = React.useState({
    name: '',
    type: 'global',
    timezone: 'UTC',
    isDefault: false,
    active: true,
  });
  const [newCalendarDraft, setNewCalendarDraft] = React.useState({
    name: '',
    type: 'global',
    timezone: 'UTC',
    isDefault: false,
    active: true,
  });

  const [weekDraft, setWeekDraft] = React.useState({
    dayOfWeek: 2,
    shiftNumber: 1,
    isWorking: true,
    dayStartTime: '08:00:00',
    dayEndTime: '17:00:00',
    workingMinutes: 480,
  });
  const [exceptionDraft, setExceptionDraft] = React.useState({
    exceptionDate: '',
    isWorking: false,
    workingMinutes: 0,
    description: '',
  });
  const [dayDraft, setDayDraft] = React.useState({
    calendarDate: '',
    isWorking: false,
    workingMinutes: 0,
    reason: '',
  });
  const [weekSearch, setWeekSearch] = React.useState('');
  const [exceptionSearch, setExceptionSearch] = React.useState('');
  const [daySearch, setDaySearch] = React.useState('');

  const resetFormDrafts = React.useCallback(() => {
    setCalendarDraft({
      name: '',
      type: 'global',
      timezone: 'UTC',
      isDefault: false,
      active: true,
    });
    setWeekDraft({
      dayOfWeek: 2,
      shiftNumber: 1,
      isWorking: true,
      dayStartTime: '08:00:00',
      dayEndTime: '17:00:00',
      workingMinutes: 480,
    });
    setExceptionDraft({
      exceptionDate: '',
      isWorking: false,
      workingMinutes: 0,
      description: '',
    });
    setDayDraft({
      calendarDate: '',
      isWorking: false,
      workingMinutes: 0,
      reason: '',
    });
    setCopyCalendarName('');
    setNewCalendarDraft({
      name: '',
      type: 'global',
      timezone: 'UTC',
      isDefault: false,
      active: true,
    });
  }, []);

  const syncCalendarDraft = React.useCallback((root: CalendarRecord | null) => {
    if (!root) return;
    setCalendarDraft({
      name: root.name ?? '',
      type: root.type ?? 'global',
      timezone: root.timezone ?? 'UTC',
      isDefault: !!root.isDefault,
      active: root.active ?? true,
    });
  }, []);

  const loadCalendarById = React.useCallback(async (calendarId: string) => {
    if (!projectId || !calendarId.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchCalendarDetails({
        projectId,
        calendarId: calendarId.trim(),
      });
      setCalendar(res.calendar ?? null);
      setWeeks(res.weeks);
      setExceptions(res.exceptions);
      setDays(res.days);
      syncCalendarDraft(res.calendar ?? null);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load calendar details.');
    } finally {
      setLoading(false);
    }
  }, [projectId, syncCalendarDraft]);

  const refreshDetails = React.useCallback(async () => {
    await loadCalendarById(calendarIdInput);
  }, [calendarIdInput, loadCalendarById]);

  const refreshCalendarOptions = React.useCallback(async () => {
    if (!projectId) return;
    setLoadingCalendarOptions(true);
    try {
      const list = await fetchProjectCalendars(projectId);
      const seen = new Set<string>();
      const options = list
        .map((c) => ({
          value: String(c.id ?? '').trim(),
          label: String(c.name || 'Unnamed'),
          calendarId: String(c.id || ''),
          isDefault: Boolean(c.isDefault),
        }))
        .filter((opt) => opt.value.length > 0)
        .filter((opt) => {
          if (seen.has(opt.value)) return false;
          seen.add(opt.value);
          return true;
        });
      setCalendarOptions(options);
    } finally {
      setLoadingCalendarOptions(false);
    }
  }, [projectId]);

  React.useEffect(() => {
    // In React StrictMode (dev), effects run twice. Guard to avoid duplicate API calls.
    if (hasLoadedCalendarListRef.current) return;
    hasLoadedCalendarListRef.current = true;
    void refreshCalendarOptions();
  }, [refreshCalendarOptions]);

  const withRefresh = async (action: () => Promise<unknown>, message: string) => {
    await action();
    notifications.show({ color: 'teal', title: 'Success', message });
    await refreshCalendarOptions();
    await refreshDetails();
  };

  const handleCalendarSelect = React.useCallback(
    async (value: string | null) => {
      const selected = value ?? '';
      setCalendarIdInput(selected);
      if (!selected) {
        setCalendar(null);
        setWeeks([]);
        setExceptions([]);
        setDays([]);
        return;
      }
      await loadCalendarById(selected);
    },
    [loadCalendarById]
  );

  const filteredWeeks = weeks.filter((w) => {
    const q = weekSearch.trim().toLowerCase();
    if (!q) return true;
    const day = DAY_OPTIONS.find((d) => Number(d.value) === w.dayOfWeek)?.label || String(w.dayOfWeek);
    return `${day} ${w.shiftNumber ?? 1} ${w.dayStartTime} ${w.dayEndTime} ${w.workingMinutes} ${w.isWorking ? 'yes' : 'no'}`
      .toLowerCase()
      .includes(q);
  });

  const filteredExceptions = exceptions.filter((ex) => {
    const q = exceptionSearch.trim().toLowerCase();
    if (!q) return true;
    return `${ex.exceptionDate} ${ex.workingMinutes} ${ex.description ?? ''} ${ex.isWorking ? 'yes' : 'no'}`
      .toLowerCase()
      .includes(q);
  });

  const filteredDays = days.filter((d) => {
    const q = daySearch.trim().toLowerCase();
    if (!q) return true;
    return `${d.calendarDate} ${d.workingMinutes} ${d.reason ?? ''} ${d.isWorking ? 'yes' : 'no'}`
      .toLowerCase()
      .includes(q);
  });

  const handleUpdateCalendar = React.useCallback(async () => {
    if (!calendar?.id) return;
    await withRefresh(
      () =>
        updateCalendar(calendar.id, {
          projectId,
          ...calendarDraft,
          name: calendarDraft.name.trim(),
          meta: calendar?.meta ?? {},
        }),
      'Calendar updated'
    );
  }, [calendar, calendarDraft, projectId]);

  const handleDeleteCalendar = React.useCallback(async () => {
    if (!calendar?.id) return;
    await deleteCalendar(calendar.id);
    notifications.show({ color: 'teal', title: 'Success', message: 'Calendar deleted' });
    setCalendarIdInput('');
    setCalendar(null);
    setWeeks([]);
    setExceptions([]);
    setDays([]);
    resetFormDrafts();
    await refreshCalendarOptions();
  }, [calendar, resetFormDrafts, refreshCalendarOptions]);

  const handleCopyCalendar = React.useCallback(async () => {
    if (!calendar) return;
    const newName = (copyCalendarName || `${calendar.name || 'Calendar'} (Copy)`).trim();
    if (!newName) return;
    const res = await createCalendar({
      projectId,
      name: newName,
      type: calendar.type || 'global',
      isDefault: false,
      timezone: calendar.timezone || 'UTC',
      active: calendar.active ?? true,
      meta: calendar.meta ?? {},
    });
    const createdId = String(res?.data ?? '').trim();
    notifications.show({ color: 'teal', title: 'Success', message: 'Calendar copied' });
    await refreshCalendarOptions();
    if (createdId) {
      setCalendarIdInput(createdId);
      await loadCalendarById(createdId);
    } else {
      setCalendarIdInput('');
      setCalendar(null);
      setWeeks([]);
      setExceptions([]);
      setDays([]);
      resetFormDrafts();
    }
  }, [calendar, copyCalendarName, projectId, refreshCalendarOptions, loadCalendarById, resetFormDrafts]);

  const handleAddWeekRule = React.useCallback(async () => {
    if (!calendar?.id) return;
    await withRefresh(
      () => createCalendarWeek({ calendarId: calendar.id, ...weekDraft }),
      'Weekly rule added'
    );
  }, [calendar, weekDraft]);

  const handleSyncWeekRule = React.useCallback(async (rule: CalendarWeekRecord) => {
    await withRefresh(() => updateCalendarWeek(rule.id, { ...rule }), 'Weekly rule synced');
  }, []);

  const handleDeleteWeekRule = React.useCallback(async (ruleId: string) => {
    await withRefresh(() => deleteCalendarWeek(ruleId), 'Weekly rule removed');
  }, []);

  const handleAddException = React.useCallback(async () => {
    if (!calendar?.id) return;
    await withRefresh(
      () => createCalendarException({ calendarId: calendar.id, ...exceptionDraft }),
      'Exception added'
    );
  }, [calendar, exceptionDraft]);

  const handleSyncException = React.useCallback(async (item: CalendarExceptionRecord) => {
    await withRefresh(() => updateCalendarException(item.id, { ...item }), 'Exception synced');
  }, []);

  const handleDeleteException = React.useCallback(async (itemId: string) => {
    await withRefresh(() => deleteCalendarException(itemId), 'Exception removed');
  }, []);

  const handleAddOverride = React.useCallback(async () => {
    if (!calendar?.id) return;
    await withRefresh(
      () => createCalendarDay({ calendarId: calendar.id, ...dayDraft }),
      'Day override added'
    );
  }, [calendar, dayDraft]);

  const handleSyncOverride = React.useCallback(async (item: CalendarDayRecord) => {
    await withRefresh(() => updateCalendarDay(item.id, { ...item }), 'Day override synced');
  }, []);

  const handleDeleteOverride = React.useCallback(async (itemId: string) => {
    await withRefresh(() => deleteCalendarDay(itemId), 'Day override removed');
  }, []);

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--mantine-color-default-border)',
          flexShrink: 0,
        }}
      >
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
          <Box style={{ minWidth: 0 }}>
            <Text size="lg" fw={600}>
              Calendar
            </Text>
            <Text size="sm" c="dimmed">
              Configure weekly rhythm, planned exceptions, and day-level overrides for your project.
            </Text>
          </Box>
          <Badge variant="light" color="violet">
            Project: {projectId || 'Not set'}
          </Badge>
        </Group>
      </Box>

      <Box
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          padding: '12px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {!projectId ? (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            No project selected. Configure project settings first.
          </Alert>
        ) : null}

        <Card withBorder radius="md" p="sm">
          <Stack gap="xs">
            <Group align="flex-end" wrap="wrap" gap="sm">
              <Select
                label="Choose calendar"
                placeholder="Select an existing calendar"
                data={calendarOptions}
                searchable
                value={calendarIdInput}
                onChange={(v) => {
                  void handleCalendarSelect(v);
                }}
                style={{ flex: 1 }}
                disabled={!projectId}
                rightSection={loadingCalendarOptions ? <IconListSearch size={14} /> : undefined}
                renderOption={({ option }) => {
                  const typed = option as typeof option & { calendarId?: string; isDefault?: boolean };
                  return (
                    <Box>
                      <Group gap={6} wrap="nowrap">
                        <Text size="sm" fw={500}>{option.label}</Text>
                        {typed.isDefault ? (
                          <Text size="10px" c="teal" fw={600}>
                            Default
                          </Text>
                        ) : null}
                      </Group>
                      <Text size="10px" c="dimmed">{typed.calendarId || option.value}</Text>
                    </Box>
                  );
                }}
              />
              <Button
                variant="light"
                disabled
              >
                Set this calendar for {projectName}
              </Button>
              <PermissionRestrictedTooltip restricted={!canCalendarCreate}>
                <Button
                  variant="default"
                  leftSection={<IconPlus size={16} />}
                  onClick={() => setCreateCalendarModalOpened(true)}
                  disabled={!projectId || !canCalendarCreate}
                >
                  Add new calendar
                </Button>
              </PermissionRestrictedTooltip>
              <Tooltip label="Refresh calendar list">
                <ActionIcon
                  variant="default"
                  onClick={refreshCalendarOptions}
                  disabled={!projectId}
                  size="lg"
                  aria-label="Refresh list"
                >
                  <IconRefresh size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
            <Text size="xs" c="dimmed">
              Pick a calendar to view and manage its work week, exceptions, and day overrides.
            </Text>
          </Stack>
        </Card>

        {error ? (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {error}
          </Alert>
        ) : null}

        <Tabs
          defaultValue="overview"
          style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconCalendarTime size={14} />}>Overview</Tabs.Tab>
            <Tabs.Tab value="week" leftSection={<IconClockEdit size={14} />}>Weekly rules</Tabs.Tab>
            <Tabs.Tab value="exceptions">Exceptions</Tabs.Tab>
            <Tabs.Tab value="days">Day overrides</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="sm" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <CalendarOverviewTab
              calendar={calendar}
              calendarDraft={calendarDraft}
              setCalendarDraft={setCalendarDraft}
              copyCalendarName={copyCalendarName}
              setCopyCalendarName={setCopyCalendarName}
              typeOptions={CALENDAR_TYPE_OPTIONS}
              timezoneOptions={TZ_OPTIONS}
              weeksCount={weeks.length}
              exceptionsCount={exceptions.length}
              overridesCount={days.length}
              onUpdateCalendar={handleUpdateCalendar}
              onDeleteCalendar={handleDeleteCalendar}
              onCopyCalendar={handleCopyCalendar}
              canEdit={canCalendarEdit}
              canDelete={canCalendarDelete}
              canCreate={canCalendarCreate}
            />
          </Tabs.Panel>

          <Tabs.Panel value="week" pt="sm" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <CalendarWeekRulesTab
              calendarSelected={Boolean(calendar?.id)}
              tableViewportHeight={TABLE_VIEWPORT_HEIGHT}
              dayOptions={DAY_OPTIONS}
              weekSearch={weekSearch}
              setWeekSearch={setWeekSearch}
              filteredWeeks={filteredWeeks}
              weekDraft={weekDraft}
              setWeekDraft={setWeekDraft}
              onAddRule={handleAddWeekRule}
              onSyncRule={handleSyncWeekRule}
              onDeleteRule={handleDeleteWeekRule}
              canCreate={canCalendarCreate}
              canEdit={canCalendarEdit}
              canDelete={canCalendarDelete}
            />
          </Tabs.Panel>

          <Tabs.Panel value="exceptions" pt="sm" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <CalendarExceptionsTab
              calendarSelected={Boolean(calendar?.id)}
              tableViewportHeight={TABLE_VIEWPORT_HEIGHT}
              exceptionSearch={exceptionSearch}
              setExceptionSearch={setExceptionSearch}
              filteredExceptions={filteredExceptions}
              exceptionDraft={exceptionDraft}
              setExceptionDraft={setExceptionDraft}
              parseDateYmd={parseDateYmd}
              formatDateYmd={formatDateYmd}
              onAddException={handleAddException}
              onSyncException={handleSyncException}
              onDeleteException={handleDeleteException}
              canCreate={canCalendarCreate}
              canEdit={canCalendarEdit}
              canDelete={canCalendarDelete}
            />
          </Tabs.Panel>

          <Tabs.Panel value="days" pt="sm" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <CalendarOverridesTab
              calendarSelected={Boolean(calendar?.id)}
              tableViewportHeight={TABLE_VIEWPORT_HEIGHT}
              daySearch={daySearch}
              setDaySearch={setDaySearch}
              filteredDays={filteredDays}
              dayDraft={dayDraft}
              setDayDraft={setDayDraft}
              parseDateYmd={parseDateYmd}
              formatDateYmd={formatDateYmd}
              onAddOverride={handleAddOverride}
              onSyncOverride={handleSyncOverride}
              onDeleteOverride={handleDeleteOverride}
              canCreate={canCalendarCreate}
              canEdit={canCalendarEdit}
              canDelete={canCalendarDelete}
            />
          </Tabs.Panel>
        </Tabs>
      </Box>
      <Modal
        opened={createCalendarModalOpened}
        onClose={() => setCreateCalendarModalOpened(false)}
        title="Add new calendar"
        centered
      >
        <Stack gap="sm">
          <TextInput
            label="Name"
            value={newCalendarDraft.name}
            onChange={(e) =>
              setNewCalendarDraft((s) => ({ ...s, name: e.currentTarget.value }))
            }
          />
          <Select
            label="Type"
            data={CALENDAR_TYPE_OPTIONS}
            value={newCalendarDraft.type}
            onChange={(v) => setNewCalendarDraft((s) => ({ ...s, type: v || 'global' }))}
          />
          <Select
            label="Timezone"
            data={TZ_OPTIONS}
            value={newCalendarDraft.timezone}
            onChange={(v) => setNewCalendarDraft((s) => ({ ...s, timezone: v || 'UTC' }))}
            searchable
          />
          <Group>
            <Checkbox
              label="Default calendar"
              checked={newCalendarDraft.isDefault}
              onChange={(e) =>
                setNewCalendarDraft((s) => ({ ...s, isDefault: e.currentTarget.checked }))
              }
            />
            <Checkbox
              label="Active"
              checked={newCalendarDraft.active}
              onChange={(e) =>
                setNewCalendarDraft((s) => ({ ...s, active: e.currentTarget.checked }))
              }
            />
          </Group>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setCreateCalendarModalOpened(false)}>
              Cancel
            </Button>
            <Button
              disabled={!projectId || !newCalendarDraft.name.trim()}
              onClick={async () => {
                const res = await createCalendar({
                  projectId,
                  ...newCalendarDraft,
                  name: newCalendarDraft.name.trim(),
                  meta: {},
                });
                const createdId = String(res?.data ?? '').trim();
                notifications.show({ color: 'teal', title: 'Success', message: 'Calendar created' });
                setCreateCalendarModalOpened(false);
                await refreshCalendarOptions();
                if (createdId) {
                  setCalendarIdInput(createdId);
                  await loadCalendarById(createdId);
                }
                setNewCalendarDraft({
                  name: '',
                  type: 'global',
                  timezone: 'UTC',
                  isDefault: false,
                  active: true,
                });
              }}
            >
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
};

export default CalendarPage;
